import React from 'react'
import { useList, useGetIdentity, useCreate, useUpdate } from '@refinedev/core'
import { App, Button, Checkbox, InputNumber, Table } from 'antd'
import { brand } from '../../theme/brand'
import { SectionCard, TableSurface, EmptyHint } from '../../theme/listkit'
import { monthLabel, averageScores, RUBRIC, type ScoreRow } from './botm'
import type { EligibilityRow } from './EligibilityTab'

interface Identity {
  id?: string
}

/** Per-criterion draft values, keyed by RUBRIC key. */
type Draft = Partial<Record<(typeof RUBRIC)[number]['key'], number | null>>

/** One committee-table row: the five RUBRIC criteria plus a synthetic Total row. */
interface CommitteeRow {
  key: 'story' | 'vouches' | 'rotation' | 'listing' | 'first_time' | 'total'
  label: string
  max: number
}

const COMMITTEE_ROWS: CommitteeRow[] = [...RUBRIC, { key: 'total', label: 'Total', max: 100 }]

/** A placeholder row for a business that has score rows but has fallen out of
 * (or never appeared in) `business_spotlight_eligibility` — e.g. it just won
 * and won't resurface there until the cooldown clears. Keeps the ballot
 * visible without pretending the checks passed. */
function unknownEligibilityRow(businessId: string): EligibilityRow {
  return {
    business_id: businessId,
    name: null,
    owner_user_id: null,
    status: null,
    days_live: null,
    live_60_days: null,
    independent_vouches: null,
    has_3_vouches: null,
    complete_listing: null,
    has_perk: null,
    strikes: null,
    no_strikes: null,
    not_distributor: null,
    fresh: null,
    last_win: null,
    no_recent_win: null,
    eligible: false,
  }
}

/**
 * Scoring tab — the committee's rubric ballots. Each business gets its own
 * card: the signed-in admin's own ballot (five bounded InputNumbers plus a
 * recusal checkbox) and a read-only committee table of everyone's scores so
 * far, plus a Total row (`averageScores(scores).total`, formatted `x / 100`).
 *
 * Lists the UNION of eligible businesses and businesses that already have
 * score rows this month — a business that just won (and so dropped out of
 * `eligible`) must stay visible here until the month is over, or the
 * committee loses the ability to see/adjust its own ballot on the winner.
 *
 * The five numeric columns and `recused` are the ONLY columns this ever writes —
 * `scorer_user_id`, `total` (generated) and `recusal_reason` are server-filled
 * (see `botm_scores`'s recusal trigger,
 * backend/supabase/migrations/20260923120200_jaguar_owned_spotlight.sql). The
 * trigger nulls out all five numeric columns whenever `recused` ends up true —
 * whether the admin checked the box themselves or the trigger auto-detected a
 * conflict (owns/vouched/nominated) — so this component never needs to null them
 * out client-side; it just reflects whatever comes back.
 */
export function ScoringTab({ month }: { month: string }) {
  const { data: identity } = useGetIdentity<Identity>()
  const myId = identity?.id

  const { result: eligResult, query: eligQuery } = useList<EligibilityRow>({
    resource: 'business_spotlight_eligibility',
    pagination: { pageSize: 500 },
  })
  const eligibilityRows = eligResult?.data ?? []

  const { result: scoresResult, query: scoresQuery } = useList<ScoreRow>({
    resource: 'botm_scores',
    filters: [{ field: 'month', operator: 'eq', value: month }],
    pagination: { pageSize: 500 },
  })
  const scores = scoresResult?.data ?? []

  const isLoading = eligQuery.isLoading || scoresQuery.isLoading

  const businesses = React.useMemo(() => {
    const byId = new Map(eligibilityRows.map((r) => [r.business_id, r]))
    const eligibleIds = eligibilityRows.filter((r) => r.eligible === true).map((r) => r.business_id)
    const scoredIds = [...new Set(scores.map((s) => s.business_id))]
    const unionIds = [...new Set([...eligibleIds, ...scoredIds])]
    const rows = unionIds.map((id) => byId.get(id) ?? unknownEligibilityRow(id))
    // Eligible businesses first (so the active ballot list leads), then name.
    rows.sort((a, b) => {
      if (a.eligible === b.eligible) return (a.name ?? '').localeCompare(b.name ?? '')
      return a.eligible ? -1 : 1
    })
    return rows
  }, [eligibilityRows, scores])

  if (!isLoading && businesses.length === 0) {
    return <EmptyHint>No businesses are eligible for {monthLabel(month)} yet.</EmptyHint>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {businesses.map((biz) => (
        <ScoreCard
          key={biz.business_id}
          month={month}
          businessId={biz.business_id}
          businessName={biz.name ?? 'Unknown business'}
          myId={myId}
          scores={scores.filter((s) => s.business_id === biz.business_id)}
        />
      ))}
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub }}>
        Averages exclude recused members. Record the final average on the spotlight when you schedule it.
      </div>
    </div>
  )
}

function ScoreCard({
  month,
  businessId,
  businessName,
  myId,
  scores,
}: {
  month: string
  businessId: string
  businessName: string
  myId: string | undefined
  scores: ScoreRow[]
}) {
  const { message } = App.useApp()
  const { mutateAsync: create } = useCreate()
  const { mutateAsync: update } = useUpdate()
  const [saving, setSaving] = React.useState(false)
  // Remembers a row this component itself just created, so a second Save
  // before the list has refetched updates that row instead of creating a
  // duplicate (botm_scores' own unique (month, business_id, scorer_user_id)
  // would reject the duplicate anyway, but this avoids the failed round-trip).
  const [createdId, setCreatedId] = React.useState<string | null>(null)

  const myRow = React.useMemo(() => scores.find((s) => s.scorer_user_id === myId), [scores, myId])
  const targetId = myRow?.id ?? createdId ?? undefined

  // A ScoreCard is keyed by businessId only (see ScoringTab), so switching the
  // console's month can hand the SAME mounted card a different month without
  // remounting it. A remembered id from the old month must not leak into a
  // save for the new one.
  React.useEffect(() => {
    setCreatedId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, businessId])

  const [draft, setDraft] = React.useState<Draft>({})
  const [recused, setRecused] = React.useState(false)

  // Sync the local ballot draft from the admin's own loaded row, mirroring
  // GivingSettingsPage's draft-synced-from-record pattern — Refine's own
  // mutate() invalidates this list on success, so a save flows straight back
  // here without a manual refetch.
  React.useEffect(() => {
    if (!myRow) return
    setDraft({
      story: myRow.story,
      vouches: myRow.vouches,
      rotation: myRow.rotation,
      listing: myRow.listing,
      first_time: myRow.first_time,
    })
    setRecused(myRow.recused)
  }, [myRow])

  const complete = RUBRIC.every((c) => draft[c.key] != null)
  const canSave = (complete || recused) && Boolean(myId) && !saving

  async function save() {
    if (!myId || saving) return
    setSaving(true)
    const payload = {
      month,
      business_id: businessId,
      story: draft.story ?? null,
      vouches: draft.vouches ?? null,
      rotation: draft.rotation ?? null,
      listing: draft.listing ?? null,
      first_time: draft.first_time ?? null,
      recused,
    }
    const mutationOpts = { successNotification: false as const, errorNotification: false as const }
    try {
      if (targetId) {
        await update({ resource: 'botm_scores', id: targetId, values: payload, ...mutationOpts })
      } else {
        const res = await create({ resource: 'botm_scores', values: payload, ...mutationOpts })
        const newId = (res as { data?: { id?: string | number } })?.data?.id
        if (newId != null) setCreatedId(String(newId))
      }
      message.success('Score saved.')
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const { byCriterion, total } = averageScores(scores)

  return (
    <SectionCard title={businessName}>
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontFamily: brand.fSans,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: brand.sub,
            marginBottom: 10,
          }}
        >
          Your score
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
          {RUBRIC.map((c) => (
            <div key={c.key}>
              <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12, color: brand.ink, marginBottom: 4 }}>
                {c.label} <span style={{ color: brand.sub, fontWeight: 500 }}>(max {c.max})</span>
              </div>
              <InputNumber
                data-testid={`score-${businessId}-${c.key}`}
                min={0}
                max={c.max}
                precision={0}
                value={draft[c.key] ?? undefined}
                onChange={(v) => setDraft((prev) => ({ ...prev, [c.key]: v as number | null }))}
                style={{ width: 110 }}
              />
            </div>
          ))}
        </div>
        <Checkbox
          data-testid={`score-${businessId}-recused`}
          checked={recused}
          onChange={(e) => setRecused(e.target.checked)}
        >
          I have a conflict with this business
        </Checkbox>
        {myRow?.recused ? (
          <div
            style={{
              marginTop: 10,
              fontFamily: brand.fSans,
              fontWeight: 600,
              fontSize: 12.5,
              color: '#a4452f',
            }}
          >
            {`You're recused from this business (${myRow.recusal_reason}). Your score doesn't count.`}
          </div>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <Button
            data-testid={`score-save-${businessId}`}
            type="primary"
            loading={saving}
            disabled={!canSave}
            onClick={save}
          >
            Save
          </Button>
        </div>
      </div>

      <div
        style={{
          fontFamily: brand.fSans,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: brand.sub,
          marginBottom: 10,
        }}
      >
        Committee table
      </div>
      <TableSurface>
        <Table<CommitteeRow> dataSource={COMMITTEE_ROWS} rowKey="key" pagination={false} scroll={{ x: true }}>
          <Table.Column<CommitteeRow> dataIndex="label" title="Criterion" />
          {scores.map((s) => (
            <Table.Column<CommitteeRow>
              key={s.id}
              title={s.scorer_user_id.slice(0, 8)}
              render={(_: unknown, row) => (
                <span data-testid={`score-cell-${businessId}-${s.id}-${row.key}`}>
                  {s.recused ? 'Recused' : (s[row.key] ?? '—')}
                </span>
              )}
            />
          ))}
          <Table.Column<CommitteeRow>
            title="Average"
            render={(_: unknown, row) => {
              const isTotal = row.key === 'total'
              const value = isTotal ? total : byCriterion[row.key]
              const text = value == null ? '—' : isTotal ? `${value} / 100` : String(value)
              return (
                <span
                  data-testid={`score-avg-${businessId}-${row.key}`}
                  style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink }}
                >
                  {text}
                </span>
              )
            }}
          />
        </Table>
      </TableSurface>
    </SectionCard>
  )
}
